import {
  defaultOrder,
  elementFieldOrder,
  Order,
  QueryConfig,
  PartialNcsConfig,
  defaultQueryConfig,
  defaultFullFrequencyConfig,
  defaultMatchingExpressionsConfig,
  defaultKwicConfig,
  defaultContextConfig, matchingOrderAllFields
} from 'ncs'

const groupsConfig = {
  'texts.grupo_edad': {
    sortBy: v => ['<19', '19-34', '35-54', '>54', 'Desconocido'].indexOf(v)
  },
  'texts.estudios': {
    sortBy: v => ['Universitarios', 'Medios', 'Primarios', 'Desconocidos'].indexOf(v)
  }
}

const frequencyGroupOrder = [
  'texts.grupo_edad',
  'texts.rol',
  'texts.sexo',
  'texts.estudios',
  'documents.subcorpus',
]

const documentTextOrder: Order[] = [
  {
    name: 'documento',
    criteria: [
      { field: 'documents.nombre_fichero_audio', asc: true }
    ]
  },
  { name: 'hablante', criteria: [{ field: 'texts.hablante', asc: true }] },
  { name: 'edad', criteria: [{ field: 'texts.edad', asc: true }] },
  { name: 'estudios', criteria: [{ field: 'texts.estudios', asc: true }] },
]

const matchingExpressionsConfig = {
  ...defaultMatchingExpressionsConfig,
  groups: groupsConfig,
  frequencyGroupOrder,
  tokens: {
    groups: ['token', 'category', 'tag', 'lemma', 'unit'],
  },
  order: {
    orders: [
      { name: 'matching', criteria: matchingOrderAllFields(5, true) },
      { name: 'elements', criteria: [{ field: 'matching_expressions.elements', asc: false }] },
      ...elementFieldOrder(5, true, 'units', 'unit'),
      ...elementFieldOrder(5, true, 'tokens', 'token'),
      ...elementFieldOrder(5, true, 'tokens', 'tag'),
      ...elementFieldOrder(5, true, 'tokens', 'lemma'),
      ...elementFieldOrder(5, true, 'tokens', 'unit')
    ],
    default: ['elements', 'matching']
  }
}

const collocationsConfig = {
  ...matchingExpressionsConfig,
  elements: {
    ...matchingExpressionsConfig.elements,
    types: ['tokens'],
    defaultType: 'tokens',
    // Exclude:
    // Preposición (X)
    // Conjunción (C)
    // Determinantes (D)
    // Pronombre (P)
    // Interjección (I)
    // Signo de puntuación (Q)
    excludeTagsPattern: '!X!C!D*!P*!I!Q',
    onlyDistances: true,
    tokens: {
      ...matchingExpressionsConfig.tokens,
      defaultGroups: ['lemma']
    }
  }
}

const interventionsConfig: QueryConfig = {
  ...defaultQueryConfig,
  endpoint: 'interventions',
  maxHeaderSize: 50, // Percentage of result table width
  defaultDisplay: 'interventions',
  order: {
    orders: documentTextOrder,
    default: ['documento']
  },
  pagination: true,
  elements: {
    types: ['texts'],
    defaultType: 'texts'
  },
  download: [
    { name: 'download.page.csv', type: 'Page', mimeType: 'text/csv', filename: 'result.csv', pageViewName: 'download' },
    {
      name: 'download.page.xlsx',
      type: 'Page',
      mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      filename: 'result.xlsx',
      pageViewName: 'download'
    }
  ]
}

const interventionConfig: QueryConfig = {
  ...defaultQueryConfig,
  endpoint: 'intervention',
  defaultDisplay: 'intervention',
  elements: {
    types: ['texts'],
    defaultType: 'texts'
  }
}

export const ncsConfig: PartialNcsConfig = {
  elements: {
    types: ['units', 'tokens', 'texts']
  },
  categories: {
    patterns: {
      A: 'A*',
      W: 'W*',
      C: 'C*',
      D: 'D*',
      I: 'I*',
      X: 'X*',
      P: 'P*',
      Q: 'Q*',
      N: 'N*',
      V: 'V*',
      ETQ_PAUSA: 'ETQ_PAUSA',
    }
  },
  tags: {
    hideLevels: ['noun_class', 'noun_subclass', 'noun_grade']
  },
  filters: [
    { field: 'result.matching_expressions.elements', operator: 'gteq', defaultByResultType: { 'matching_expressions': 1, 'collocations': 4 } },
    { field: 'documents.subcorpus' },
    { field: 'texts.grupo_edad' },
    { field: 'texts.edad', id: 'texts.edad_from', operator: 'gteq' },
    { field: 'texts.edad', id: 'texts.edad_to', operator: 'lteq' },
    { field: 'texts.rol' },
    { field: 'texts.estudios' },
    { field: 'texts.sexo' },
    { field: 'units.searchable_info', operator: 'array_overlaps' },
    { field: 'tokens.searchable_info', operator: 'array_overlaps' },
    { field: 'texts.searchable_info', operator: 'array_overlaps' },
    { field: 'units.language', default: 'Español' },
    { field: 'tokens.language', default: 'Español' },
    { field: 'texts.hablante', operator: 'eq_any' },
  ],
  result: {
    defaultType: 'kwic',
    error: {
      contactLink: './contacto',
      contactEmail: 'support@nlpgo.com'
    },
    types: ['frequency', 'full_frequency', 'kwic', 'matching_expressions', 'interventions'],
    detailTypes: ['context', 'intervention'],
  },
  queries: {
    kwic: {
      ...defaultKwicConfig,
      order: {
        orders: [...defaultOrder(5), ...documentTextOrder],
        default: ['matching', 'documento']
      }
    },
    full_frequency: {
      ...defaultFullFrequencyConfig,
      groups: groupsConfig,
      frequencyGroupOrder,
    },
    matching_expressions: matchingExpressionsConfig,
    collocations: collocationsConfig,
    context: {
      ...defaultContextConfig,
      textHeader: true,
      tokens: {
        sourceText: true,
        groups: [],
        defaultGroups: []
      }
    },
    interventions: interventionsConfig,
    intervention: interventionConfig,
  }
}
